import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Tabs,
    Tab,
    styled,
    TabProps,
} from '@mui/material';
import ActivityBar from '../ActivityBar/ActivityBar';
import SectionSlider from '../SectionSlider/SectionSlider';
import { sections, sectionPaths } from '../../../constants/sectionData';
import profileImg from '../../../assets/SVGs/profile_pic.svg';
import { apiService } from '../../../api/apiService';
import { getFileImagePath } from '../utils/fileUtils';
import ProjectManagementFileView from '../Popup/ProjectManagementFileView';
import UserMultiSubFileViewPopup from '../Popup/UserMultiSubFileViewPopup';
// import UserMultiFileViewPopup from '../Popup/UserMultiFileViewPopup';
// import UserMultiSubFileViewPopup from '../Popup/UserMultiSubFileViewPopup';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import useFilter, { FilterValues } from '../../../hooks/useFilter';
import FilterComponent from '../utils/FilterUtils/FilterUtils';


// Styled Tabs Component
const StyledTabs = styled(Tabs)({
    '& .MuiTabs-flexContainer': {
        gap: '10px',
    },
});

// Styled Tab Component
const StyledTab = styled((props: TabProps & { selected?: boolean }) => <Tab {...props} />)(({ theme, selected }) => ({
    borderRadius: '11px 11px 0px 0px',
    backgroundColor: '#FEFEFE',
    color: selected ? '#000' : '#000',
    padding: '15px',
    transition: 'background-color 0.3s, color 0.3s',
}));

interface DocumentData {
    id: number;
    doc_path: string;
    file_name: string;
    file_size: string;
}

interface SelectedDocument {
    projectId: number;
    doc_path: string;
    file_name: string;
    file_size: string;
    function_l1: string;
    current_project: string;
    created_at: any;
}

interface ProjectData {
    id: number;
    management: string;
    function_l1: string;
    section_name: string;
    created_by: string;
    created_at: string;
    files: DocumentData[];
}

interface UserSectionMultiSubTabViewProps {
    fetchAllFilesApi?: string;
    fetchFileViewApi?: any;
    highlightSection?: any;
    verticalTabs?: { label: string; value: string; subTabs?: { label: string; value: string }[] }[];
    levels?: number;

}

export default function UserSectionMultiSubTab({ fetchAllFilesApi, fetchFileViewApi, highlightSection, verticalTabs = [], levels }: UserSectionMultiSubTabViewProps) {
    const { instance } = useMsal();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const management = localStorage.getItem("management") || "Prague & India GCC";
    const [verticalTabValue, setVerticalTabValue] = useState(verticalTabs[0]?.value || "a");
    const [subTabValue, setSubTabValue] = useState<string | null>(null);
    const [routineData, setRoutineData] = useState<ProjectData[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<SelectedDocument | null>(null);

    // console.log(id, 'id');

    const handleCardClick = (projectId: number, file: DocumentData) => {
        console.log('projectId', projectId)
        const project = routineData.find((project) => project.id === projectId);

        setSelectedDocument({
            ...file,
            projectId, // Include the project id for reference
            function_l1: project?.function_l1 || '',
            current_project: project?.section_name || '',
            created_at: project?.created_at || '',
        });
    };

    const fetchRoutineData = async (filters?: FilterValues) => {
        setIsLoading(true);
        setError(null);
        try {
            const currentTab = verticalTabs.find((tab) => tab.value === verticalTabValue);
            const subTabs = currentTab?.subTabs || [];
            const selectedSubTab = subTabs.find((subTab) => subTab.value === subTabValue);

            // Determine the section_name based on whether subtabs exist and are selected
            const sectionName = selectedSubTab ? selectedSubTab.label : currentTab?.label;

            const response = await apiService(
                `${fetchAllFilesApi}`,
                "GET",
                {
                    management: management,
                    section_name: sectionName,
                    ...(filters || {})
                },
                undefined, 
                instance as PublicClientApplication
            );

            if (response.data) {
                setRoutineData(response.data);
            }
        } catch (error) {
            console.error("Error fetching routine data:", error);
            setRoutineData([]);
            if (error instanceof Error) {
                setError(error.message || "An error occurred");
            } else {
                setError("An error occurred");
            }
            if (error instanceof Error && (error as any).response && (error as any).response.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };

     const { filterValues, applyFilter, resetFilter } = useFilter();
    
        const handleApplyFilter = (filters: FilterValues) => {
            const newFilters = applyFilter(filters);
            fetchRoutineData({
                ...newFilters,
                function_l1: newFilters.function_l1 || '',
                function_l2: newFilters.function_l2 || '',
                function_l3: newFilters.function_l3 || '',
                function_l4: newFilters.function_l4 || '',
            });
        };

    useEffect(() => {
        fetchRoutineData();
    }, [verticalTabValue, subTabValue]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setVerticalTabValue(newValue);
        setSubTabValue(null);
    };

    const handleSubTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setSubTabValue(newValue);
    };

    const breadcrumbs = [
        <Link key="1" color="#000" to="/">
            Home
        </Link>,
        <Link key="2" color="#000" to="/operations/management">
            Evidence Upload
        </Link>,
        <Link key="3" color="#000" to="/operations/management">
            Management
        </Link>,
        <Typography key="4" sx={{ fontFamily: 'Avantt' }}>
            {management}
        </Typography>,
    ];

    const currentTab = verticalTabs.find((tab) => tab.value === verticalTabValue);
    const subTabs = currentTab?.subTabs || [];

    return (
        <>
            <ActivityBar breadcrumbs={breadcrumbs} />
            <Box sx={{ width: '100%' }}>
                <SectionSlider
                    sections={sections}
                    sectionPaths={sectionPaths}
                    highlightSection={highlightSection}
                    highlightColor="#D7A833"
                    defaultColor="#666"
                />
                <Box sx={{ mx: 3, borderRadius: '11px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            backgroundColor: '#F5F4F7',
                            borderRadius: '11px',
                        }}
                    >
                        <StyledTabs
                            value={verticalTabValue}
                            onChange={handleTabChange}
                            aria-label="vertical tabs"
                        >
                            {verticalTabs.map((tab) => (
                                <StyledTab
                                    key={tab.value}
                                    label={tab.label}
                                    value={tab.value}
                                />
                            ))}
                        </StyledTabs>

                        <FilterComponent
                            levels={levels ?? 0}
                            onApplyFilter={handleApplyFilter}
                            initialValues={filterValues}
                        />
                    </Box>
                    {subTabs.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                backgroundColor: '#F5F4F7',
                                borderRadius: '11px',
                                mt: 2,
                            }}
                        >
                            <StyledTabs
                                value={subTabValue || subTabs[0]?.value}
                                onChange={handleSubTabChange}
                                aria-label="sub tabs"
                            >
                                {subTabs.map((subTab) => (
                                    <StyledTab
                                        key={subTab.value}
                                        label={subTab.label}
                                        value={subTab.value}
                                    />
                                ))}
                            </StyledTabs>
                        </Box>
                    )}
                    {isLoading ? (
                        <Typography>Loading...</Typography>
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : routineData.length === 0 ? ( // Check if routineData is empty
                        <Typography sx={{ textAlign: 'center', mt: 4 }}>Data not found</Typography>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', overflowY: 'auto', maxHeight: '80vh', background: '#FEFEFE', pt: 2 }}>
                            {routineData.map((project) => (
                                project.files.map((file) => (
                                    <Card
                                        sx={{ maxWidth: 345, margin: '20px', borderRadius: '11px' }}
                                        key={`${project.id}-${file.doc_path}`} // Use project id and doc_path as the key
                                    >
                                        <CardActionArea onClick={() => handleCardClick(project.id, file)}>
                                            <CardMedia
                                                component="img"
                                                image={getFileImagePath(file.doc_path)}
                                                sx={{ padding: '16px', borderRadius: '11px', height: '100px', width: '100px' }}
                                            />
                                            <CardContent>
                                                <Typography variant="body1">
                                                    {file.file_name.split('_').slice(5).join('_')}
                                                </Typography>
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    sx={{ paddingTop: '10px', paddingBottom: '10px' }}
                                                >
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        Created on {new Date(project.created_at).toLocaleDateString()}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {file.file_size}
                                                    </Typography>
                                                </Box>
                                                {/* <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        display: 'flex',
                                                        gap: '4px',
                                                        pt: '10px',
                                                    }}
                                                >
                                                    You opened:{' '}
                                                    <Typography variant="body2" sx={{ color: 'blue' }}>
                                                        Just Now
                                                    </Typography>
                                                </Typography> */}
                                                <Box sx={{ paddingTop: '10px', display: 'flex', gap: '10px' }}>
                                                    {/* <Box>
                                                        <img src={profileImg} width={35} height={35} alt="" />
                                                    </Box> */}
                                                    <Box sx={{ display: 'grid', gap: '1px' }}>
                                                        <Typography sx={{ color: '#1e1e1e80', fontSize: '11px' }}>Created by</Typography>
                                                        <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>{project.created_by}</Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                ))
                            ))}

                            {selectedDocument && (
                             
                             <ProjectManagementFileView
                                    onClose={() => setSelectedDocument(null)}
                                    open={true}
                                    name={selectedDocument.file_name}
                                    id={selectedDocument.projectId} // Use project id
                                    functionl1={selectedDocument.function_l1}
                                    current_project={selectedDocument.current_project}
                                    date={selectedDocument.created_at}
                                    sectionTitle={verticalTabs.find(tab => tab.value === verticalTabValue)?.label || ''}
                                    userDetail={true}
                                    apiPath={`/management/project_management/${selectedDocument.projectId}`}
                                    getFileImagePath={getFileImagePath}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );
}